/**
 * Carga de cada ambiente na marcenaria (leve, normal ou pesado), marcada pelo
 * PCP para a Capacidade da Fábrica. A planilha não guarda isso: vem do
 * backend, ligado pela chave "PEDIDO:AMBIENTE" (`chaveAmbiente`), que segue
 * as renomeações do Conciliar do Mapa.
 *
 * Uma request para a página inteira (`/capacidade/excecoes`), num cache de
 * módulo compartilhado pela Capacidade da Fábrica, pelo painel do ambiente e
 * pelo Conciliar (mesmo desenho de `useProjetistasExecutivo`). Quem marca
 * recebe de volta só os ambientes alterados e o cache é atualizado no lugar.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { lerExcecoesCapacidade, marcarExcecoesCapacidade } from "../services/mapaProducao";

/** Mesmos gates do backend (`mapa_producao/excecoes.py` e `views_excecoes.py`). */
export const PERM_VER_EXCECAO = "capacidade_fabrica:view";
export const PERM_MARCAR_EXCECAO = "capacidade_fabrica:marcar_excecao";
/**
 * O Conciliar do Mapa (`mapa_producao:gerir`) também lê as marcas: o ambiente
 * que só tem marca de carga precisa aparecer como órfão para ser religado. Só
 * ele pede este gate; o resto da tela segue com `PERM_VER_EXCECAO`.
 */
export const PERMS_LER_EXCECAO_PARA_RELIGAR = Object.freeze([PERM_VER_EXCECAO, "mapa_producao:gerir"]);
const PERMS_PADRAO = Object.freeze([PERM_VER_EXCECAO]);
const ERRO_PADRAO = "Não foi possível ler as marcas do PCP.";

const VALIDADE_MS = 60_000;
const VAZIO = Object.freeze({
  excecoes: Object.freeze({}),
  podeMarcar: false,
  carregado: false,
  erro: null,
});

const cache = { dono: null, estado: VAZIO, lidoEm: 0, emAndamento: null, request: null };
const ouvintes = new Set();

function publicar(estado) {
  cache.estado = estado;
  ouvintes.forEach((ouvinte) => ouvinte(estado));
}

function buscar(request, dono) {
  if (cache.emAndamento && cache.dono === dono) return cache.emAndamento;
  cache.dono = dono;
  cache.request = request;
  // Promise.resolve: um `request` que falhe antes de devolver promessa vira
  // rejeição tratada abaixo, e não erro solto dentro do efeito.
  cache.emAndamento = Promise.resolve()
    .then(() => lerExcecoesCapacidade(request))
    .then((resposta) => {
      if (cache.dono !== dono) return;
      cache.lidoEm = Date.now();
      publicar({
        excecoes: resposta?.excecoes && typeof resposta.excecoes === "object" ? resposta.excecoes : {},
        podeMarcar: Boolean(resposta?.pode_marcar),
        carregado: true,
        erro: null,
      });
    })
    // Sem a resposta a página segue só com a contagem (régua oficial), mas
    // diz que não leu: lista vazia passaria por "ninguém marcou nada" e o PCP
    // remarcaria tudo. A próxima tentativa só depois da validade (não repete
    // em loop) ou no "Tentar de novo".
    .catch((erro) => {
      cache.lidoEm = Date.now();
      if (cache.dono === dono) {
        publicar({ ...cache.estado, carregado: true, erro: erro?.message || ERRO_PADRAO });
      }
    })
    .finally(() => {
      cache.emAndamento = null;
    });
  return cache.emAndamento;
}

/** Releitura imediata (depois de religar um ambiente renomeado, por exemplo). */
export function recarregarExcecoesCapacidade() {
  if (!cache.request) return Promise.resolve();
  cache.lidoEm = 0;
  return buscar(cache.request, cache.dono);
}

/** Só para testes: esquece o que foi lido. */
export function limparCacheDeExcecoes() {
  cache.dono = null;
  cache.estado = VAZIO;
  cache.lidoEm = 0;
  cache.emAndamento = null;
  cache.request = null;
}

/**
 * `{ excecoes, podeMarcar, carregado, erro, permitido, marcar, recarregar }`.
 * `erro` = mensagem quando a leitura falhou (aí `excecoes` não quer dizer
 * "sem marca"). `marcar(itens, {classe, motivo, observacao})`: `itens` =
 * [{chave, rotulo}]. Rejeita com a mensagem do backend em `err.message`.
 * `permissoes` = basta uma delas para ler (padrão: a da página).
 */
export default function useExcecoesCapacidade({ ativo = true, permissoes = PERMS_PADRAO } = {}) {
  const { request, usuario, hasPermission } = useAuth() || {};
  const permitido =
    ativo &&
    Boolean(usuario) &&
    typeof hasPermission === "function" &&
    permissoes.some((codigo) => hasPermission(codigo));
  const dono = usuario?.id ?? null;
  const [estado, setEstado] = useState(() => (cache.dono === dono ? cache.estado : VAZIO));

  useEffect(() => {
    if (!permitido) return undefined;
    ouvintes.add(setEstado);
    if (cache.dono !== dono) {
      cache.estado = VAZIO;
      cache.lidoEm = 0;
    }
    setEstado(cache.estado);
    if (Date.now() - cache.lidoEm > VALIDADE_MS) buscar(request, dono);
    return () => {
      ouvintes.delete(setEstado);
    };
  }, [permitido, request, dono]);

  const marcar = useCallback(
    async (itens, { classe, motivo, observacao } = {}) => {
      const resposta = await marcarExcecoesCapacidade(request, { itens, classe, motivo, observacao });
      if (cache.dono === dono) {
        publicar({
          ...cache.estado,
          excecoes: { ...cache.estado.excecoes, ...(resposta?.excecoes || {}) },
        });
      }
      return resposta;
    },
    [request, dono],
  );

  // Sem a permissão de ver a página não há o que esperar: `carregado` vem
  // verdadeiro e `permitido` falso, para quem mostra o bloco saber esconder.
  const acoes = { marcar, recarregar: recarregarExcecoesCapacidade };
  return permitido ? { ...estado, permitido: true, ...acoes } : { ...VAZIO, carregado: true, permitido: false, ...acoes };
}
